import { useState, useEffect } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Label, Select } from "../components/UI";
import { allZones, saveZone, getSavedZone } from "../components/HeaderClock";
import { getNotifPrefs, saveNotifPrefs, NOTIF_CATEGORIES } from "../lib/notifPrefs";
import { Globe2, CalendarDays, Info, BellRing, Volume2 } from "lucide-react";
import { toast } from "sonner";

const Toggle = ({ on, onChange, testId }) => (
  <button type="button" data-testid={testId} role="switch" aria-checked={on} onClick={() => onChange(!on)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-[#2E7D32]" : "bg-[#D7CCC8]"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

export default function Preferences() {
  const { user } = useAuth();
  const [tz, setTz] = useState(() => getSavedZone(user));
  const [dateFormat, setDateFormat] = useState(user?.preferences?.date_format || "MM/DD/YYYY");
  const [notif, setNotif] = useState(() => {
    const local = getNotifPrefs();
    const srv = user?.preferences?.notifications;
    return srv ? { ...local, ...srv, categories: { ...local.categories, ...(srv.categories || {}) } } : local;
  });
  const [perm, setPerm] = useState(() => ("Notification" in window ? Notification.permission : "unsupported"));
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get("/app-meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { document.title = "Preferences | COCOA EHR"; }, []);

  const save = async () => {
    try {
      await api.put("/me/preferences", { timezone: tz, date_format: dateFormat, notifications: notif });
      saveZone(tz);
      saveNotifPrefs(notif);
      toast.success("Preferences saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const requestPerm = () => { if ("Notification" in window) Notification.requestPermission().then(setPerm); };
  const testNotif = () => {
    if (!("Notification" in window) || Notification.permission !== "granted") return toast.error("Enable browser notifications first");
    try { new Notification("Cocoa EHR - Test notification", { body: "This is how alert popups will look." }); } catch { /* blocked */ }
  };
  const setCat = (k, v) => setNotif((n) => ({ ...n, categories: { ...n.categories, [k]: v } }));

  const permChip = perm === "granted"
    ? <span className="rounded-full bg-[#E8F5E9] px-2.5 py-1 text-[10px] font-bold text-[#2E7D32]" data-testid="notif-perm-chip">BROWSER: ALLOWED</span>
    : perm === "denied"
      ? <span className="rounded-full bg-[#FFEBEE] px-2.5 py-1 text-[10px] font-bold text-[#C62828]" data-testid="notif-perm-chip">BROWSER: BLOCKED</span>
      : <span className="rounded-full bg-[#FFF8E1] px-2.5 py-1 text-[10px] font-bold text-[#876600]" data-testid="notif-perm-chip">BROWSER: NOT ENABLED</span>;

  return (
    <div data-testid="preferences-page" className="max-w-2xl">
      <PageHeader title="Preferences" subtitle="Personal display & notification settings for your account" />
      <Card className="p-6 space-y-5">
        <div>
          <Label className="flex items-center gap-1.5"><Globe2 size={13} /> Timezone</Label>
          <Select data-testid="pref-timezone-select" value={tz} onChange={(e) => setTz(e.target.value)}>
            {[...new Set([tz, ...allZones()])].map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
          </Select>
          <p className="mt-1 text-xs text-[#7B6157]">Used for the header clock and time displays. Clinical timestamps are always stored in UTC for audit integrity.</p>
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><CalendarDays size={13} /> Date format</Label>
          <Select data-testid="pref-date-format-select" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
            <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
          </Select>
        </div>
      </Card>

      <Card className="mt-5 p-6 space-y-4" data-testid="pref-notifications-card">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="flex items-center gap-1.5 mb-0"><BellRing size={13} /> Browser notifications</Label>
          {permChip}
          {perm === "default" && <Btn variant="outline" className="rounded-lg px-3 py-1.5 text-xs" data-testid="notif-enable-btn" onClick={requestPerm}>Enable</Btn>}
          {perm === "granted" && <Btn variant="ghost" className="rounded-lg px-3 py-1.5 text-xs" data-testid="notif-test-btn" onClick={testNotif}>Send test</Btn>}
        </div>
        {perm === "denied" && <p className="text-xs text-[#C62828]">Notifications are blocked for this site. Allow them in your browser's site settings, then reload.</p>}
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-[#3E2723]">Show popup notifications</div>
            <p className="text-xs text-[#7B6157]">Desktop popups for new alerts while Cocoa EHR is open in a tab. Clicking a popup takes you to the item.</p>
          </div>
          <Toggle testId="notif-master-toggle" on={notif.enabled} onChange={(v) => setNotif((n) => ({ ...n, enabled: v }))} />
        </div>
        <div className={notif.enabled ? "" : "opacity-40 pointer-events-none"}>
          <div className="text-xs font-bold uppercase tracking-wide text-[#84675B] mb-2">Categories</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {NOTIF_CATEGORIES.map(([k, l]) => (
              <div key={k} className="flex items-center justify-between rounded-xl border border-[#F1EEE7] px-3 py-2">
                <span className="text-sm text-[#3E2723]">{l}</span>
                <Toggle testId={`notif-cat-${k}`} on={notif.categories[k] !== false} onChange={(v) => setCat(k, v)} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-[#3E2723]"><Volume2 size={13} /> Notification sound</div>
            <p className="text-xs text-[#7B6157]">Play a soft chime when a popup fires. Off by default.</p>
          </div>
          <Toggle testId="notif-sound-toggle" on={notif.sound} onChange={(v) => setNotif((n) => ({ ...n, sound: v }))} />
        </div>
      </Card>

      <div className="mt-5 flex justify-end"><Btn data-testid="pref-save-btn" onClick={save}>Save Preferences</Btn></div>

      {meta && (
        <Card className="mt-5 p-5 flex items-start gap-3 text-sm text-[#5D4037]" data-testid="pref-about">
          <Info size={16} className="mt-0.5 shrink-0 text-[#7B6157]" />
          <span>Cocoa EHR v{meta.version} · released {meta.released_at}. Questions? Reach support at {meta.support?.email} or {meta.support?.phone}.</span>
        </Card>
      )}
    </div>
  );
}
